<!--
	SPDX-FileCopyrightText: Ferdinand Thiessen <opensource@fthiessen.de>
	SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<NcSettingsSection :name="t('security_guard', 'Security guard')">
		<NcEmptyContent
			v-if="isLoading"
			:name="t('security_guard', 'Loading system status')">
			<template #icon>
				<NcLoadingIcon :size="64" />
			</template>
		</NcEmptyContent>
		<NcEmptyContent
			v-else-if="Object.keys(rawSetupChecks).length === 0"
			:name="t('security_guard', 'System status not available')">
			<template #icon>
				<NcIconSvgWrapper :path="mdiCloudOff" :size="64" />
			</template>
			<template #action>
				<NcButton type="primary" @click="loadSetupChecks(true)">
					{{ t('security_guard', 'Load system status') }}
				</NcButton>
			</template>
		</NcEmptyContent>
		<div v-else :class="$style.wrapper">
			<div :class="$style.container">
				<SetupCheckCategory
					v-for="categoryId in CATEGORIES"
					:key="categoryId"
					:category="categoryId"
					:category-name="getCategoryName(categoryId)"
					:setup-checks="setupChecksForCategory(categoryId)"
					@show-details="onShowDetails" />
			</div>
			<NcButton type="tertiary" @click="loadSetupChecks(true)">
				{{ t('security_guard', 'Refresh system status') }}
			</NcButton>
		</div>
		<DetailsModal
			:open.sync="isDetailsModalOpen"
			:category-name="getCategoryName(currentCategoryId)"
			:current-entry="currentActiveEntry"
			:entries="setupChecksForCategory(currentCategoryId)" />
	</NcSettingsSection>
</template>

<script setup lang="ts">
import type { ICategorizedSetupChecks, ISetupCheck } from '../types'

import { mdiCloudOff } from '@mdi/js'
import { showError } from '@nextcloud/dialogs'
import { translate as t } from '@nextcloud/l10n'
import { generateOcsUrl } from '@nextcloud/router'
import { onBeforeMount, ref } from 'vue'
import { CATEGORIES, CATEGORY_NAMES } from '../constants.ts'

import axios from '@nextcloud/axios'
import NcButton from '@nextcloud/vue/dist/Components/NcButton.js'
import NcEmptyContent from '@nextcloud/vue/dist/Components/NcEmptyContent.js'
import NcIconSvgWrapper from '@nextcloud/vue/dist/Components/NcIconSvgWrapper.js'
import NcLoadingIcon from '@nextcloud/vue/dist/Components/NcLoadingIcon.js'
import NcSettingsSection from '@nextcloud/vue/dist/Components/NcSettingsSection.js'
import DetailsModal from './modal/DetailsModal.vue'
import SetupCheckCategory from './SetupCheckCategory.vue'

const isLoading = ref(true)
const rawSetupChecks = ref<ICategorizedSetupChecks>({})

/**
 * Load setup checks
 * @param disableCache Set to true to disable cache
 */
const loadSetupChecks = async (disableCache = false) => {
	try {
		isLoading.value = true
		const { data } = await axios.get(
			generateOcsUrl('apps/security_guard/results?forced={forced}', {
				forced: disableCache.toString(),
			}),
		)
		rawSetupChecks.value = data.ocs.data.results
	} catch (error) {
		console.error('Failed to load setup checks', error)
		showError(t('security_guard', 'Could not load results please try again'))
	} finally {
		isLoading.value = false
	}
}

onBeforeMount(loadSetupChecks)

const setupChecksForCategory = (category: string) =>
	Object.values(rawSetupChecks.value[category] ?? {})

const getCategoryName = (id: string) =>
	(CATEGORY_NAMES as Record<string, string | undefined>)[id] ?? id

/// Handling of the modal:

/**
 * Currently show entry in the modal
 */
const currentActiveEntry = ref<ISetupCheck>()

/**
 * Is the details modal open
 */
const isDetailsModalOpen = ref(false)

const currentCategoryId = ref<string>(CATEGORIES[0])

const onShowDetails = (categoryId: string, check?: ISetupCheck) => {
	currentActiveEntry.value = check
	currentCategoryId.value = categoryId
	isDetailsModalOpen.value = true
}
</script>

<style module lang="scss">
// The inner container for each list of startup checks
.container {
	display: flex;
	flex-direction: row;
	gap: 22px;
	justify-content: space-around;
	width: 100%;
}

// The outer wrapper providing the scrollbar
.wrapper {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
}

@media only screen and (max-width: 768px) {
	.container {
		flex-direction: column;
		gap: unset;
	}
}
</style>
